import { ChevronRight, Circle, CircleCheck, CircleDot, Plus, X } from "lucide-react";

export type SimpleTaskListItemLeftIndicator = "none" | "empty" | "dot" | "check";
export type SimpleTaskListItemRightIndicator = "none" | "chevron" | "close" | "plus";

type Props = {
  icon: React.ReactNode;
  title: React.ReactNode;
  meta?: React.ReactNode;
  details?: React.ReactNode;
  right?: React.ReactNode;
  selected?: boolean;
  dimmed?: boolean;
  color?: string;
  leftIndicator?: SimpleTaskListItemLeftIndicator;
  rightIndicator?: SimpleTaskListItemRightIndicator;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onRightIndicatorClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const leftIndicatorIcons = {
  empty: Circle,
  dot: CircleDot,
  check: CircleCheck,
} as const;

const rightIndicatorIcons = {
  chevron: ChevronRight,
  close: X,
  plus: Plus,
} as const;

const rightIndicatorLabels = {
  chevron: "Open",
  close: "Remove",
  plus: "Add",
} as const;

export default function SimpleTaskListItem({
  icon,
  title,
  meta,
  details,
  right,
  selected = false,
  dimmed = false,
  color,
  leftIndicator = "none",
  rightIndicator = "none",
  onClick,
  onRightIndicatorClick,
}: Props) {
  const LeftIndicatorIcon = leftIndicator === "none" ? null : leftIndicatorIcons[leftIndicator];
  const RightIndicatorIcon = rightIndicator === "none" ? null : rightIndicatorIcons[rightIndicator];

  return (
    <div
      className={`group grid grid-cols-[auto_minmax(0,1fr)_auto] items-start bg-card transition-colors hover:bg-accent/30 ${
        selected ? "ring-1 ring-inset ring-foreground/20" : ""
      } ${dimmed ? "opacity-60" : ""}`}
      style={color ? { color } : undefined}
    >
      <button
        type="button"
        onClick={onClick}
        className={`col-span-2 grid min-w-0 text-left ${
          LeftIndicatorIcon
            ? "grid-cols-[auto_auto_minmax(0,1fr)]"
            : "grid-cols-[auto_minmax(0,1fr)]"
        }`}
      >
        {LeftIndicatorIcon && (
          <span className="py-3 pl-3">
            <LeftIndicatorIcon className="h-4 w-4" aria-hidden="true" />
          </span>
        )}
        <span className="px-3 py-3">{icon}</span>
        <span className="min-w-0 py-2.5 pr-2">
          <span className="block truncate text-sm font-medium">{title}</span>
          {details && <span className="mt-0.5 block truncate text-xs opacity-70">{details}</span>}
        </span>
      </button>
      <div className="flex min-w-24 max-w-36 shrink-0 flex-col items-end gap-1 px-2 py-2">
        {right}
        {RightIndicatorIcon && (
          <button
            type="button"
            className="grid h-7 w-7 place-items-center rounded-full transition-colors hover:bg-accent/50"
            onClick={event => {
              event.stopPropagation();
              onRightIndicatorClick?.(event);
            }}
            aria-label={rightIndicatorLabels[rightIndicator as Exclude<SimpleTaskListItemRightIndicator, "none">]}
            title={rightIndicatorLabels[rightIndicator as Exclude<SimpleTaskListItemRightIndicator, "none">]}
          >
            <RightIndicatorIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        {meta && <span className="max-w-full truncate text-right text-[11px] font-mono tabular-nums opacity-70">{meta}</span>}
      </div>
    </div>
  );
}
